import { useState, useEffect, useRef, useCallback, useContext } from "react";
import Table from "../../../ui/Table";
import Modal from "../../../ui/Modal";
import Loader from "../../../ui/Loader";
import ModalTableInput from "../../../ui/ModalTableInput";

import { AuthContext } from "../../../../context/AuthContext";
import {
  swalConfirm,
  swalError,
  swalSuccess,
  swalWarning,
} from "../../../../utils/swalAlert";
import { fetchWithAuth } from "../../../../utils/api";
const serverUrl = `${process.env.REACT_APP_SERVER_URL}`;

const ManagePrograms = () => {
  const [programs, setPrograms] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [newProgram, setNewProgram] = useState({
    name: "",
    departmentId: "",
    durationYears: "",
    poDescription: "",
    programOutcomes: [{ code: "", description: "", targetAttainment: 0 }],
    PSOutcomes: [{ code: "", description: "", targetAttainment: 0 }],
  });
  const { user } = useContext(AuthContext);

  const [editProgram, setEditProgram] = useState({
    id: "",
    name: "",
    departmentId: "",
    durationYears: "",
    poDescription: "",
    programOutcomes: [],
    PSOutcomes: [],
    original: {},
  });
  const [initialEditProgram, setInitialEditProgram] = useState(null);
  const [isEditChanged, setIsEditChanged] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const hasFetched = useRef(false);

  // Prepare table data
  const getTableData = () => {
    const tableData = [];

    programs.forEach((program) => {
      tableData.push({
        _id: program._id,
        type: "program",
        name: program.name,
        departmentName: program.departmentName,
        durationYears: program.durationYears,
        programId: program._id,
        programData: program,
      });
    });

    return tableData;
  };

  const tableData = getTableData();

  const columns = [
    {
      key: "name",
      label: "Program Name",
      sortable: false,
      render: (item) => {
        if (item.type === "program") {
          return <span style={{ fontWeight: "600" }}>{item.name}</span>;
        }
        return <span></span>;
      },
    },
    {
      key: "departmentName",
      label: "Department",
      sortable: false,
      render: (item) => {
        if (item.type === "program") {
          return <span>{item.departmentName}</span>;
        }
        return <span></span>;
      },
    },
    {
      key: "durationYears",
      label: "Duration (Years)",
      sortable: false,
      render: (item) => {
        if (item.type === "program") {
          return <span>{item.durationYears}</span>;
        }
        return <span></span>;
      },
    },
    // Remove outcomeCode and outcomeDescription columns as outcomes are only shown in the edit modal
    {
      key: "edit",
      label: "",
      sortable: false,
      render: (item) => {
        if (item.type === "program") {
          return (
            <button
              className="edit-btn"
              onClick={() => handleEditClick(item.programData)}
            >
              <i className="fa-solid fa-pen-to-square"></i>
            </button>
          );
        }
        return null;
      },
    },
    {
      key: "delete",
      label: "",
      sortable: false,
      render: (item) => {
        if (item.type === "program") {
          return (
            <button
              className="delete-btn"
              onClick={() => handleDeleteProgram(item.programId)}
            >
              <i className="fa-solid fa-trash-can"></i>
            </button>
          );
        }
        return null;
      },
    },
  ];

  const fetchPrograms = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetchWithAuth(
        `${serverUrl}/api/program/get-all/${user.instituteId}`,
        {
          credentials: "include",
        }
      );
      if (!response) return;
      if (!response.ok) throw new Error("Failed to fetch Programs");
      const data = await response.json();

      // Create lookup maps for departments
      const departmentMap = new Map(
        departments.map((dept) => [dept._id, dept.name])
      );

      const programsWithFormatting = await Promise.all(
        data.map(async (program) => {
          // Handle both cases: when API returns populated objects or just IDs
          let departmentName = "No Department";

          if (program.departmentId) {
            if (
              typeof program.departmentId === "object" &&
              program.departmentId.name
            ) {
              // Already populated
              departmentName = program.departmentId.name;
            } else if (typeof program.departmentId === "string") {
              // Just ID, lookup from our departments array first
              departmentName = departmentMap.get(program.departmentId);
              // If not found in local data, fetch individually
              if (!departmentName) {
                departmentName = await fetchDepartmentName(
                  program.departmentId
                );
              }
            }
          }

          return {
            ...program,
            outcomesCount: program.PSOutcomes?.length || 0,
            departmentName,
          };
        })
      );

      setPrograms(programsWithFormatting);
      setError(null);
    } catch (err) {
      setError(err.message);
      swalError(err.message);
    } finally {
      setLoading(false);
    }
  }, [user.instituteId, departments]);

  const fetchDepartments = useCallback(async () => {
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/department/get-all/${user.instituteId}`,
        {
          credentials: "include",
        }
      );
      if (!response) return;
      if (!response.ok) throw new Error("Failed to fetch Departments");
      const data = await response.json();
      setDepartments(data);
    } catch (err) {
      console.error("Error fetching departments:", err.message);
    }
  }, [user.instituteId]);



  // Helper function to fetch department name by ID
  const fetchDepartmentName = async (departmentId) => {
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/department/get/${departmentId}`,
        {
          credentials: "include",
        }
      );
      if (!response || !response.ok) return "Unknown Department";
      const data = await response.json();
      return data.name || "Unknown Department";
    } catch (err) {
      console.error("Error fetching department:", err.message);
      return "Unknown Department";
    }
  };

  useEffect(() => {
    if (hasFetched.current) return;

    const fetchAllData = async () => {
      // First fetch departments
      await Promise.all([fetchDepartments()]);
      // Then fetch programs (which depends on the above data)
      await fetchPrograms();
    };

    fetchAllData();
    hasFetched.current = true;
  }, [fetchPrograms, fetchDepartments]);

  // Handle outcome changes for create/edit form
  const updateOutcomeEntry = (index, field, value, isEdit = false, type = "PSO") => {
    const targetState = isEdit ? editProgram : newProgram;
    const setter = isEdit ? setEditProgram : setNewProgram;
    const key = type === "PO" ? "programOutcomes" : "PSOutcomes";

    const updatedOutcomes = [...targetState[key]];
    updatedOutcomes[index] = {
      ...updatedOutcomes[index],
      [field]: value,
    };

    setter((prev) => ({
      ...prev,
      [key]: updatedOutcomes,
    }));
  };

  const addOutcomeEntry = (isEdit = false, type = "PSO") => {
    const setter = isEdit ? setEditProgram : setNewProgram;
    const key = type === "PO" ? "programOutcomes" : "PSOutcomes";

    setter((prev) => ({
      ...prev,
      [key]: [...prev[key], { code: "", description: "", targetAttainment: 0 }],
    }));
  };

  const removeOutcomeEntry = (index, isEdit = false, type = "PSO") => {
    const targetState = isEdit ? editProgram : newProgram;
    const setter = isEdit ? setEditProgram : setNewProgram;
    const key = type === "PO" ? "programOutcomes" : "PSOutcomes";

    if (targetState[key].length <= 1) {
      swalWarning("At least one outcome is required.");
      return;
    }

    const updatedOutcomes = targetState[key].filter((_, i) => i !== index);
    setter((prev) => ({
      ...prev,
      [key]: updatedOutcomes,
    }));
  };

  const validateOutcomes = (outcomes, typeName = "Outcome") => {
    if (!outcomes || outcomes.length === 0) {
      swalError(`At least one ${typeName} is required.`);
      return false;
    }

    for (let i = 0; i < outcomes.length; i++) {
      if (!outcomes[i].code.trim() || !outcomes[i].description.trim()) {
        swalError(`${typeName} ${i + 1}: Both code and description are required.`);
        return false;
      }
      if (
        outcomes[i].targetAttainment === undefined ||
        outcomes[i].targetAttainment === null ||
        outcomes[i].targetAttainment < 0 ||
        outcomes[i].targetAttainment > 3
      ) {
        swalError(
          `${typeName} ${i + 1}: Target Attainment must be between 0 and 3.`
        );
        return false;
      }
    }

    const codes = outcomes.map((outcome) => outcome.code.trim());
    const uniqueCodes = new Set(codes);
    if (codes.length !== uniqueCodes.size) {
      swalError(`Each ${typeName} must have a unique code.`);
      return false;
    }

    return true;
  };

  const handleCreateProgram = async (e) => {
    e.preventDefault();

    if (!validateOutcomes(newProgram.programOutcomes, "Program Outcome")) {
      return;
    }

    if (!validateOutcomes(newProgram.PSOutcomes, "Program Specific Outcome")) {
      return;
    }

    // Validate required fields: duration
    if (
      !newProgram.durationYears ||
      String(newProgram.durationYears).trim() === ""
    ) {
      swalError("Duration is required.");
      return;
    }

    setIsSubmitting(true);

    try {
      const programData = {
        ...newProgram,
        instituteId: user.instituteId,
        departmentId: newProgram.departmentId || null,
        durationYears: newProgram.durationYears
          ? Number(newProgram.durationYears)
          : null,
      };

      const response = await fetchWithAuth(`${serverUrl}/api/program/add`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(programData),
      });
      if (!response) return;

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Failed to create Program");
      }
      const message = data.message || "Program created successfully.";
      swalSuccess(message);
      fetchPrograms();
    } catch (error) {
      swalError("Failed to create Program");
    } finally {
      setIsSubmitting(false);
      setIsCreateModalOpen(false);
      setNewProgram({
        name: "",
        departmentId: "",
        durationYears: "",
        poDescription: "",
        programOutcomes: [{ code: "", description: "", targetAttainment: 0 }],
        PSOutcomes: [{ code: "", description: "", targetAttainment: 0 }],
      });
    }
  };

  const handleDeleteProgram = async (programId) => {
    const result = await swalConfirm(
      "Are you sure you want to delete this Program?",
      "",
      "Yes, delete it!",
      "No, Cancel"
    );

    if (result.isConfirmed) {
      try {
        const response = await fetchWithAuth(
          `${serverUrl}/api/program/delete/${programId}`,
          {
            method: "DELETE",
            credentials: "include",
          }
        );
        if (!response) return;
        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.message || "Failed to delete Program");
        }
        setPrograms((prevPrograms) =>
          prevPrograms.filter((program) => program._id !== programId)
        );
        swalSuccess("Program deleted successfully.");
      } catch (err) {
        swalError(err.message || "Failed to delete Program");
      }
    }
  };

  const handleEditProgram = async (e) => {
    e.preventDefault();

    if (!validateOutcomes(editProgram.programOutcomes, "Program Outcome")) {
      return;
    }

    if (!validateOutcomes(editProgram.PSOutcomes, "Program Specific Outcome")) {
      return;
    }

    // Validate required fields: duration for edit
    if (
      !editProgram.durationYears ||
      String(editProgram.durationYears).trim() === ""
    ) {
      swalError("Duration is required.");
      return;
    }

    setIsSubmitting(true);

    try {
      const programData = {
        name: editProgram.name,
        departmentId: editProgram.departmentId || null,
        durationYears: editProgram.durationYears
          ? Number(editProgram.durationYears)
          : null,
        poDescription: editProgram.poDescription,
        programOutcomes: editProgram.programOutcomes,
        PSOutcomes: editProgram.PSOutcomes,
      };

      const response = await fetchWithAuth(
        `${serverUrl}/api/program/update/${editProgram.id}`,
        {
          method: "PUT",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(programData),
        }
      );
      if (!response) return;
      const data = await response.json();
      if (!response.ok)
        throw new Error(data.message || "Failed to update Program");
      const message = data.message || "Program updated successfully.";
      swalSuccess(message);
      fetchPrograms();
    } catch (error) {
      swalError("Failed to update Program");
    } finally {
      setIsSubmitting(false);
      setIsEditModalOpen(false);
      setEditProgram({
        id: "",
        name: "",
        departmentId: "",
        durationYears: "",
        poDescription: "",
        programOutcomes: [],
        PSOutcomes: [],
        original: {},
      });
      setInitialEditProgram(null);
      setIsEditChanged(false);
    }
  };

  const handleEditClick = (program) => {
    // Extract the ID properly whether it's a populated object or just an ID
    const departmentId =
      typeof program.departmentId === "object"
        ? program.departmentId?._id
        : program.departmentId;

    setEditProgram({
      id: program._id,
      name: program.name,
      departmentId: departmentId || "",
      durationYears: program.durationYears || "",
      poDescription: program.poDescription || "",
      programOutcomes:
        program.programOutcomes && program.programOutcomes.length > 0
          ? [...program.programOutcomes]
          : [{ code: "", description: "", targetAttainment: 0 }],
      PSOutcomes:
        program.PSOutcomes && program.PSOutcomes.length > 0
          ? [...program.PSOutcomes]
          : [{ code: "", description: "", targetAttainment: 0 }],
      original: {
        name: program.name,
        departmentId: program.departmentId,
        durationYears: program.durationYears,
        poDescription: program.poDescription,
        programOutcomes: program.programOutcomes,
        PSOutcomes: program.PSOutcomes,
      },
    });

    setInitialEditProgram({
      name: program.name,
      departmentId: departmentId || "",
      durationYears: program.durationYears || "",
      poDescription: program.poDescription || "",
      programOutcomes:
        program.programOutcomes && program.programOutcomes.length > 0
          ? [...program.programOutcomes]
          : [{ code: "", description: "", targetAttainment: 0 }],
      PSOutcomes:
        program.PSOutcomes && program.PSOutcomes.length > 0
          ? [...program.PSOutcomes]
          : [{ code: "", description: "", targetAttainment: 0 }],
    });

    setIsEditModalOpen(true);
    setIsEditChanged(false);
  };

  // Handle Edit Input Change
  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditProgram((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  useEffect(() => {
    if (editProgram && initialEditProgram) {
      const isNameChanged = editProgram.name !== initialEditProgram.name;
      const isDeptChanged =
        editProgram.departmentId !== initialEditProgram.departmentId;
      const isDurationChanged =
        editProgram.durationYears !== initialEditProgram.durationYears;
      const isTemplateChanged =
        editProgram.poDescription !== initialEditProgram.poDescription;
      const isPOsChanged =
        JSON.stringify(editProgram.programOutcomes) !==
        JSON.stringify(initialEditProgram.programOutcomes);
      const isPSOsChanged =
        JSON.stringify(editProgram.PSOutcomes) !==
        JSON.stringify(initialEditProgram.PSOutcomes);

      setIsEditChanged(
        isNameChanged ||
        isDeptChanged ||
        isDurationChanged ||
        isTemplateChanged ||
        isPOsChanged ||
        isPSOsChanged
      );
    } else {
      setIsEditChanged(false);
    }
  }, [editProgram, initialEditProgram]);

  return (
    <div>
      <div className="header">
        <button
          className="create-btn"
          onClick={() => setIsCreateModalOpen(true)}
        >
          Add Program +
        </button>
      </div>
      <div className="manage-container">
        {loading && <Loader />}
        {error && <div className="error-message">{error}</div>}
        {!loading && !error && (
          <Table data={tableData} columns={columns} caption="Programs" />
        )}
      </div>

      {/* Add Program Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => !isSubmitting && setIsCreateModalOpen(false)}
        maxWidth="50%"
      >
        <h2>Add New Program</h2>
        <p className="modal-description" style={{ marginBottom: "1rem" }}>
          Fill in the details below to add a new Program.
        </p>
        <form onSubmit={handleCreateProgram} className="program-form">
          <div className="form-group">
            <label>
              <i className="fa-solid fa-asterisk requiredIcon"></i> Program Name
            </label>
            <input
              type="text"
              value={newProgram.name}
              required
              placeholder="Enter program name"
              onChange={(e) =>
                setNewProgram({ ...newProgram, name: e.target.value })
              }
              disabled={isSubmitting}
            />
          </div>

          <div className="form-group">
            <label>Department</label>
            <select
              value={newProgram.departmentId}
              onChange={(e) =>
                setNewProgram({ ...newProgram, departmentId: e.target.value })
              }
              disabled={isSubmitting}
            >
              <option value="" disabled>
                Select Department (Optional)
              </option>
              {departments.map((dept) => (
                <option key={dept._id} value={dept._id}>
                  {dept.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>
              <i className="fa-solid fa-asterisk requiredIcon"></i> Duration
              (Years)
            </label>
            <input
              type="number"
              min="0"
              step="0.5"
              value={newProgram.durationYears}
              placeholder="Enter duration in years"
              onChange={(e) =>
                setNewProgram({
                  ...newProgram,
                  durationYears: e.target.value,
                })
              }
              disabled={isSubmitting}
              required
            />
          </div>

          <div className="form-group">
            <label>Program Description</label>
            <textarea
              value={newProgram.poDescription}
              placeholder="Enter Program description"
              onChange={(e) =>
                setNewProgram({ ...newProgram, poDescription: e.target.value })
              }
              disabled={isSubmitting}
              rows="3"
              style={{ width: "100%", padding: "0.5rem" }}
            />
          </div>

          <div className="form-group">
            <label>
              <i className="fa-solid fa-asterisk requiredIcon"></i> Program
              Outcomes
            </label>
            <ModalTableInput
              headers={[
                {
                  key: "code",
                  label: "Code",
                  width: "0.8fr",
                  required: true,
                  placeholder: "PO1",
                },
                {
                  key: "description",
                  label: "Description",
                  width: "2fr",
                  required: true,
                  placeholder: "Description",
                },
                {
                  key: "targetAttainment",
                  label: "Target",
                  width: "0.8fr",
                  required: true,
                  placeholder: "0",
                },
                {
                  key: "actions",
                  label: "",
                  width: "0.5fr",
                },
              ]}
              rows={newProgram.programOutcomes}
              onRowChange={(index, field, value) =>
                updateOutcomeEntry(index, field, value, false, "PO")
              }
              onRowRemove={(index) => removeOutcomeEntry(index, false, "PO")}
              onRowAdd={() => addOutcomeEntry(false, "PO")}
              canRemove={true}
              addButtonText="Add PO"
              renderCell={(header, row, rowIndex) => {
                switch (header.key) {
                  case "code":
                    return (
                      <input
                        type="text"
                        value={row.code}
                        placeholder={header.placeholder}
                        onChange={(e) =>
                          updateOutcomeEntry(
                            rowIndex,
                            "code",
                            e.target.value,
                            false,
                            "PO"
                          )
                        }
                        disabled={isSubmitting}
                        required
                        className="modal-table-input"
                      />
                    );
                  case "description":
                    return (
                      <input
                        type="text"
                        value={row.description}
                        placeholder={header.placeholder}
                        onChange={(e) =>
                          updateOutcomeEntry(
                            rowIndex,
                            "description",
                            e.target.value,
                            false,
                            "PO"
                          )
                        }
                        disabled={isSubmitting}
                        required
                        className="modal-table-input"
                      />
                    );
                  case "targetAttainment":
                    return (
                      <input
                        type="number"
                        min="0"
                        max="3"
                        step="0.1"
                        value={row.targetAttainment}
                        placeholder={header.placeholder}
                        onChange={(e) =>
                          updateOutcomeEntry(
                            rowIndex,
                            "targetAttainment",
                            e.target.value,
                            false,
                            "PO"
                          )
                        }
                        disabled={isSubmitting}
                        required
                        className="modal-table-input"
                      />
                    );
                  case "actions":
                    return (
                      <button
                        type="button"
                        onClick={() => removeOutcomeEntry(rowIndex, false, "PO")}
                        disabled={
                          newProgram.programOutcomes.length <= 1 || isSubmitting
                        }
                        className="modal-table-remove-btn"
                        title="Remove"
                      >
                        <i className="fa-solid fa-trash"></i>
                      </button>
                    );
                  default:
                    return null;
                }
              }}
            />
          </div>

          <div className="form-group">
            <label>
              <i className="fa-solid fa-asterisk requiredIcon"></i> Program
              Specific Outcomes
            </label>
            <ModalTableInput
              headers={[
                {
                  key: "code",
                  label: "Code",
                  width: "0.8fr",
                  required: true,
                  placeholder: "PSO1",
                },
                {
                  key: "description",
                  label: "Description",
                  width: "2fr",
                  required: true,
                  placeholder: "Description",
                },
                {
                  key: "targetAttainment",
                  label: "Target",
                  width: "0.8fr",
                  required: true,
                  placeholder: "0",
                },
                {
                  key: "actions",
                  label: "",
                  width: "0.5fr",
                },
              ]}
              rows={newProgram.PSOutcomes}
              onRowChange={(index, field, value) =>
                updateOutcomeEntry(index, field, value, false, "PSO")
              }
              onRowRemove={(index) => removeOutcomeEntry(index, false, "PSO")}
              onRowAdd={() => addOutcomeEntry(false, "PSO")}
              canRemove={true}
              addButtonText="Add PSO"
              renderCell={(header, row, rowIndex) => {
                switch (header.key) {
                  case "code":
                    return (
                      <input
                        type="text"
                        value={row.code}
                        placeholder={header.placeholder}
                        onChange={(e) =>
                          updateOutcomeEntry(
                            rowIndex,
                            "code",
                            e.target.value,
                            false,
                            "PSO"
                          )
                        }
                        disabled={isSubmitting}
                        required
                        className="modal-table-input"
                      />
                    );

                  case "description":
                    return (
                      <input
                        type="text"
                        value={row.description}
                        placeholder={header.placeholder}
                        onChange={(e) =>
                          updateOutcomeEntry(
                            rowIndex,
                            "description",
                            e.target.value,
                            false,
                            "PSO"
                          )
                        }
                        disabled={isSubmitting}
                        required
                        className="modal-table-input"
                      />
                    );

                  case "targetAttainment":
                    return (
                      <input
                        type="number"
                        min="0"
                        max="3"
                        step="0.1"
                        value={row.targetAttainment}
                        placeholder={header.placeholder}
                        onChange={(e) =>
                          updateOutcomeEntry(
                            rowIndex,
                            "targetAttainment",
                            e.target.value,
                            false,
                            "PSO"
                          )
                        }
                        disabled={isSubmitting}
                        required
                        className="modal-table-input"
                      />
                    );

                  case "actions":
                    return (
                      <button
                        type="button"
                        onClick={() => removeOutcomeEntry(rowIndex, false, "PSO")}
                        disabled={
                          newProgram.PSOutcomes.length <= 1 || isSubmitting
                        }
                        className="modal-table-remove-btn"
                        title="Remove"
                      >
                        <i className="fa-solid fa-trash"></i>
                      </button>
                    );

                  default:
                    return null;
                }
              }}
            />
          </div>

          {isSubmitting ? (
            <Loader />
          ) : (
            <button type="submit" className="btn-submit">
              Create Program
            </button>
          )}
        </form>
      </Modal>

      {/* Edit Program Modal */}
      <Modal
        isOpen={isEditModalOpen}
        maxWidth="50%"
        onClose={() => {
          if (!isSubmitting) {
            setIsEditModalOpen(false);
            setEditProgram({
              id: "",
              name: "",
              departmentId: "",
              durationYears: "",
              poDescription: "",
              programOutcomes: [],
              PSOutcomes: [],
              original: {},
            });
            setInitialEditProgram(null);
            setIsEditChanged(false);
          }
        }}
      >
        <h2>Edit Program</h2>
        <p className="modal-description" style={{ marginBottom: "1rem" }}>
          Update the details below to edit the Program information.
        </p>
        <form onSubmit={handleEditProgram} className="program-form">
          <div className="form-group">
            <label>
              <i className="fa-solid fa-asterisk requiredIcon"></i> Program Name
            </label>
            <input
              type="text"
              name="name"
              value={editProgram?.name || ""}
              required
              placeholder="Enter program name"
              onChange={handleEditInputChange}
              disabled={isSubmitting}
            />
          </div>

          <div className="form-group">
            <label>Department</label>
            <select
              name="departmentId"
              value={editProgram?.departmentId || ""}
              onChange={handleEditInputChange}
              disabled={isSubmitting}
            >
              <option value="" disabled>
                Select Department (Optional)
              </option>
              {departments.map((dept) => (
                <option key={dept._id} value={dept._id}>
                  {dept.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>
              <i className="fa-solid fa-asterisk requiredIcon"></i> Duration
              (Years)
            </label>
            <input
              type="number"
              min="0"
              step="0.5"
              name="durationYears"
              value={editProgram?.durationYears || ""}
              placeholder="Enter duration in years"
              onChange={handleEditInputChange}
              disabled={isSubmitting}
              required
            />
          </div>

          <div className="form-group">
            <label>Program Description</label>
            <textarea
              name="poDescription"
              value={editProgram?.poDescription || ""}
              placeholder="Enter Program description"
              onChange={handleEditInputChange}
              disabled={isSubmitting}
              rows="3"
              style={{ width: "100%", padding: "0.5rem" }}
            />
          </div>

          <div className="form-group">
            <label>
              <i className="fa-solid fa-asterisk requiredIcon"></i> Program
              Outcomes
            </label>
            <ModalTableInput
              headers={[
                {
                  key: "code",
                  label: "Code",
                  width: "0.8fr",
                  required: true,
                  placeholder: "PO1",
                },
                {
                  key: "description",
                  label: "Description",
                  width: "2fr",
                  required: true,
                  placeholder: "Description",
                },
                {
                  key: "targetAttainment",
                  label: "Target",
                  width: "0.8fr",
                  required: true,
                  placeholder: "0",
                },
                {
                  key: "actions",
                  label: "",
                  width: "0.5fr",
                },
              ]}
              rows={editProgram.programOutcomes}
              onRowChange={(index, field, value) =>
                updateOutcomeEntry(index, field, value, true, "PO")
              }
              onRowRemove={(index) => removeOutcomeEntry(index, true, "PO")}
              onRowAdd={() => addOutcomeEntry(true, "PO")}
              canRemove={true}
              addButtonText="Add PO"
              renderCell={(header, row, rowIndex) => {
                switch (header.key) {
                  case "code":
                    return (
                      <input
                        type="text"
                        value={row.code}
                        placeholder={header.placeholder}
                        onChange={(e) =>
                          updateOutcomeEntry(
                            rowIndex,
                            "code",
                            e.target.value,
                            true,
                            "PO"
                          )
                        }
                        disabled={isSubmitting}
                        required
                        className="modal-table-input"
                      />
                    );
                  case "description":
                    return (
                      <input
                        type="text"
                        value={row.description}
                        placeholder={header.placeholder}
                        onChange={(e) =>
                          updateOutcomeEntry(
                            rowIndex,
                            "description",
                            e.target.value,
                            true,
                            "PO"
                          )
                        }
                        disabled={isSubmitting}
                        required
                        className="modal-table-input"
                      />
                    );
                  case "targetAttainment":
                    return (
                      <input
                        type="number"
                        min="0"
                        max="3"
                        step="0.1"
                        value={row.targetAttainment}
                        placeholder={header.placeholder}
                        onChange={(e) =>
                          updateOutcomeEntry(
                            rowIndex,
                            "targetAttainment",
                            e.target.value,
                            true,
                            "PO"
                          )
                        }
                        disabled={isSubmitting}
                        required
                        className="modal-table-input"
                      />
                    );
                  case "actions":
                    return (
                      <button
                        type="button"
                        onClick={() => removeOutcomeEntry(rowIndex, true, "PO")}
                        disabled={
                          editProgram.programOutcomes.length <= 1 || isSubmitting
                        }
                        className="modal-table-remove-btn"
                        title="Remove"
                      >
                        <i className="fa-solid fa-trash"></i>
                      </button>
                    );
                  default:
                    return null;
                }
              }}
            />
          </div>

          <div className="form-group">
            <label>
              <i className="fa-solid fa-asterisk requiredIcon"></i> Program
              Specific Outcomes
            </label>
            <ModalTableInput
              headers={[
                {
                  key: "code",
                  label: "Code",
                  width: "0.8fr",
                  required: true,
                  placeholder: "PSO1",
                },
                {
                  key: "description",
                  label: "Description",
                  width: "2fr",
                  required: true,
                  placeholder: "Description",
                },
                {
                  key: "targetAttainment",
                  label: "Target",
                  width: "0.8fr",
                  required: true,
                  placeholder: "0",
                },
                {
                  key: "actions",
                  label: "",
                  width: "0.5fr",
                },
              ]}
              rows={editProgram.PSOutcomes}
              onRowChange={(index, field, value) =>
                updateOutcomeEntry(index, field, value, true, "PSO")
              }
              onRowRemove={(index) => removeOutcomeEntry(index, true, "PSO")}
              onRowAdd={() => addOutcomeEntry(true, "PSO")}
              canRemove={true}
              addButtonText="Add PSO"
              renderCell={(header, row, rowIndex) => {
                switch (header.key) {
                  case "code":
                    return (
                      <input
                        type="text"
                        value={row.code}
                        placeholder={header.placeholder}
                        onChange={(e) =>
                          updateOutcomeEntry(
                            rowIndex,
                            "code",
                            e.target.value,
                            true,
                            "PSO"
                          )
                        }
                        disabled={isSubmitting}
                        required
                        className="modal-table-input"
                      />
                    );

                  case "description":
                    return (
                      <input
                        type="text"
                        value={row.description}
                        placeholder={header.placeholder}
                        onChange={(e) =>
                          updateOutcomeEntry(
                            rowIndex,
                            "description",
                            e.target.value,
                            true,
                            "PSO"
                          )
                        }
                        disabled={isSubmitting}
                        required
                        className="modal-table-input"
                      />
                    );

                  case "targetAttainment":
                    return (
                      <input
                        type="number"
                        min="0"
                        max="3"
                        step="0.1"
                        value={row.targetAttainment}
                        placeholder={header.placeholder}
                        onChange={(e) =>
                          updateOutcomeEntry(
                            rowIndex,
                            "targetAttainment",
                            e.target.value,
                            true,
                            "PSO"
                          )
                        }
                        disabled={isSubmitting}
                        required
                        className="modal-table-input"
                      />
                    );

                  case "actions":
                    return (
                      <button
                        type="button"
                        onClick={() => removeOutcomeEntry(rowIndex, true, "PSO")}
                        disabled={
                          editProgram.PSOutcomes.length <= 1 || isSubmitting
                        }
                        className="modal-table-remove-btn"
                        title="Remove"
                      >
                        <i className="fa-solid fa-trash"></i>
                      </button>
                    );

                  default:
                    return null;
                }
              }}
            />
          </div>

          {isSubmitting ? (
            <Loader />
          ) : (
            <button
              type="submit"
              className="btn-submit"
              disabled={!isEditChanged}
              onClick={(e) => {
                if (!isEditChanged) {
                  e.preventDefault();
                  swalWarning("No changes to be saved!");
                }
              }}
            >
              Update Program
            </button>
          )}
        </form>
      </Modal>
    </div>
  );
};

export default ManagePrograms;
